// src/components/shared/MotionCard.tsx
import React from "react";
import { useMotionSafe } from "../../hooks/useMotionSafe";

interface MotionCardProps {
  children: React.ReactNode;
  className?: string;
  hoverEffect?: "none" | "shadow" | "lift" | "scale" | "glow";
  hoverColor?: "blue" | "green" | "purple" | "red" | "yellow";
  clickable?: boolean;
  showBottomBar?: boolean;
  bottomBarColor?: "blue" | "green" | "purple" | "red" | "yellow";
  as?: "div" | "article" | "section";
  onClick?: () => void;
}

const MotionCard: React.FC<MotionCardProps> = ({
  children,
  className = "",
  hoverEffect = "shadow",
  hoverColor = "blue",
  clickable = false,
  showBottomBar = false,
  bottomBarColor = "blue",
  as: Component = "div",
  onClick,
}) => {
  const { shouldAnimate, getHoverClass, getAnimationClass } = useMotionSafe();

  // בסיס לכרטיס
  const baseClasses =
    "bg-white rounded-lg border border-gray-200 overflow-hidden";

  // אפקטי hover מותנים
  const getHoverEffectClasses = () => {
    if (!shouldAnimate) return "";

    switch (hoverEffect) {
      case "shadow":
        return "hover:shadow-lg transition-shadow duration-200";
      case "lift":
        return "hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300";
      case "scale":
        return "hover:shadow-lg transform hover:scale-105 transition-all duration-200";
      case "glow":
        return `hover:shadow-${hoverColor}-200 hover:shadow-xl transition-all duration-200`;
      default:
        return "";
    }
  };

  // צבעי bottom bar
  const getBottomBarColor = () => {
    const colorMap = {
      blue: "from-blue-500 to-blue-600",
      green: "from-green-500 to-green-600",
      purple: "from-purple-500 to-purple-600",
      red: "from-red-500 to-red-600",
      yellow: "from-yellow-500 to-yellow-600",
    };
    return colorMap[bottomBarColor];
  };

  // קלאסים של clickable
  const clickableClasses = clickable ? "cursor-pointer" : "";

  const finalClasses = `
    ${baseClasses}
    ${getHoverClass(getHoverEffectClasses())}
    ${clickableClasses}
    ${className}
  `.trim();

  return (
    <Component
      className={finalClasses}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {children}

      {/* Bottom Bar עם אנימציה */}
      {showBottomBar && shouldAnimate && (
        <div
          className={`h-1 bg-gradient-to-r ${getBottomBarColor()} ${getAnimationClass(
            "transform scale-x-0 group-hover:scale-x-100 transition-transform origin-right"
          )}`}
        />
      )}
    </Component>
  );
};

// רכיבי עזר נוספים
interface MotionButtonProps {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  className?: string;
  onClick?: () => void; // <-- עכשיו זה אופציונלי
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit" | "reset"; // <-- הוסף את זה
}

export const MotionButton: React.FC<MotionButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  onClick,
  disabled = false,
  loading = false,
}) => {
  const { getHoverClass, getAnimationClass } = useMotionSafe();

  const baseClasses =
    "inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2";

  const variantClasses = {
    primary: "bg-blue-600 text-white focus:ring-blue-500",
    secondary: "bg-gray-100 text-gray-900 focus:ring-gray-500",
    outline:
      "border border-gray-300 bg-white text-gray-700 focus:ring-blue-500",
  };

  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  const hoverClasses = {
    primary: getHoverClass("hover:bg-blue-700 transition-colors"),
    secondary: getHoverClass("hover:bg-gray-200 transition-colors"),
    outline: getHoverClass("hover:bg-gray-50 transition-colors"),
  };

  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "";

  return (
    <button
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${hoverClasses[variant]}
        ${disabledClasses}
        ${className}
      `.trim()}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && (
        <div
          className={`w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 ${getAnimationClass(
            "animate-spin"
          )}`}
        />
      )}
      {children}
    </button>
  );
};

// רכיב לאיקון עם אנימציה
interface MotionIconProps {
  children: React.ReactNode;
  hoverEffect?: "bounce" | "rotate" | "scale" | "pulse";
  className?: string;
}

export const MotionIcon: React.FC<MotionIconProps> = ({
  children,
  hoverEffect = "scale",
  className = "",
}) => {
  const { getHoverClass } = useMotionSafe();

  const hoverEffects = {
    bounce: getHoverClass("hover:animate-bounce"),
    rotate: getHoverClass("hover:rotate-12 transition-transform"),
    scale: getHoverClass("hover:scale-110 transition-transform"),
    pulse: getHoverClass("hover:animate-pulse"),
  };

  return (
    <div className={`inline-block ${hoverEffects[hoverEffect]} ${className}`}>
      {children}
    </div>
  );
};

export default MotionCard;
